import { IEquipment, TCreateEquipmentMaintenanceBlankDTO } from '../../../equipment.types.ts'
import useHttpLoader from '../../../../../shared/hooks/useHttpLoader.ts'
import SidePage from '../../../../../shared/SidePage'
import useStateWithChange from '../../../../../shared/hooks/useStateWithChange.ts'
import equipmentUtils from '../../../equipment.utils.ts'
import FormField from '../../../../../shared/Forms/FormField'
import Button from '../../../../../shared/Buttons/Button'
import SeparateLine from '../../../../../shared/Forms/SeparateLine'
import TextArea from '../../../../../shared/Inputs/TextArea'
import useMaintenanceBlankSchema from '../../../../Processes/Maintenance/hooks/useMaintenanceBlankSchema.ts'
import formsUtils from '../../../../../shared/Forms/forms.utils.ts'
import equipmentApi from '../../../equipment.api.ts'
import equipmentEvents from '../../../events/equipment/equipment.events.ts'
import { useState } from 'react'
import useDroparea from '../../../../../shared/Files/hooks/useDroparea.ts'
import Toggle from '../../../../../shared/Inputs/Toggle'
import DropArea from '../../../../../shared/Files/Droparea'
import PlusIcon from '../../../../../assets/icons/plus.svg?react'
import useInputFile from '../../../../../shared/hooks/useInputFile.ts'
import AttachmentLine from '../../../../../shared/Files/Attachment/AttachmentLine'
import css from './сreateUnplannedMaintenanceBlankSidePage.module.css'

interface IProps {
  open: boolean
  equipmentId: IEquipment['id']
  onClose: () => void
}

const CreateUnplannedMaintenanceBlankSidePage = (props: IProps) => {
  const { wait, loading } = useHttpLoader()

  const [filesBlockVisible, setFilesBlockVisible] = useState(true)
  const [files, setFiles] = useState<File[]>([])
  const [blank, setBlank, handleChange] = useStateWithChange<TCreateEquipmentMaintenanceBlankDTO>(
    equipmentUtils.generateEmptyUnplannedMaintenanceBlank(props.equipmentId)
  )
  const dropAreaCtrl = useDroparea({ onFiles: (files) => setFiles((prev) => [...prev, ...files]) })
  const fileInputHandler = useInputFile({ onFiles: (files) => setFiles((prev) => [...prev, ...files]) })

  const blankSchema = useMaintenanceBlankSchema(blank.blankSchemaId)

  const handleBlankValueChange = (index: number) => (val: any, key: string) => {
    setBlank((prev) => ({
      ...prev,
      values: formsUtils.arrays.updateItem(prev.values, { [key]: val } as any, index),
    }))
  }

  const renderBlankFields = () => {
    if (!blankSchema || !blankSchema.fields.length) {
      return null
    }

    return (
      <>
        <SeparateLine />
        <h5 className="text-heading-5">Бланк технического обслуживания</h5>
        {blankSchema.fields.map((fieldSchema, index) => (
          <FormField label={fieldSchema.name} labelTooltipText={fieldSchema.instruction}>
            <TextArea
              value={blank.values[index].value}
              name="value"
              onChange={handleBlankValueChange(index)}
              placeHolder="Заполните поле"
            />
          </FormField>
        ))}
      </>
    )
  }

  const renderFilesBlock = () => {
    // @ts-ignore
    return (
      <div className="flex-lines gap16" style={{ marginTop: '16px', marginBottom: '16px' }}>
        <Button
          size="default"
          type="default"
          text="Добавить файл"
          fullWidth={true}
          icon={<PlusIcon />}
          onClick={fileInputHandler}
        />
        <div>
          {files.map((f, index) => (
            <AttachmentLine
              index={index}
              file={f}
              onDelete={() => setFiles((prev) => formsUtils.arrays.removeItem(prev, index))}
            />
          ))}
        </div>
      </div>
    )
  }

  const handleSave = () => {
    wait(equipmentApi.createEquipmentMaintenanceBlank(blank, files), (resp) => {
      if (resp.status === 'success') {
        equipmentEvents.maintenanceBlankCreated(resp.body)
        props.onClose()
      }
    })
  }

  return (
    <SidePage onClose={props.onClose} open={props.open}>
      <div className="flex-lines gap16" style={{ minHeight: '100%' }}>
        <h2 className="text-heading-4">Добавить запись о внеплановом обслуживании</h2>
        <div className={css.descriptionGrid}>
          <p className="text-nm text-secondary">Тип обслуживания:</p>
          <p className="text-nm">Внеплановое</p>
        </div>
        <FormField label="Комментарий" required={true}>
          <TextArea value={blank.comment} name="comment" onChange={handleChange} placeHolder="Введите комментарий" />
        </FormField>
        {renderBlankFields()}
        <SeparateLine />
        <DropArea
          onDrop={dropAreaCtrl.handleDrop}
          isActive={dropAreaCtrl.over}
          onDragOver={dropAreaCtrl.handleDragOver}
          onDragEnter={dropAreaCtrl.handleDragEnter}
          onDragLeave={dropAreaCtrl.handleDragLeave}
        >
          <ToggleLine
            text="Прикрепленные файлы"
            active={filesBlockVisible}
            name="filesBlock"
            onChange={setFilesBlockVisible}
          />

          {filesBlockVisible && renderFilesBlock()}
        </DropArea>

        <div style={{ marginTop: 'auto' }}>
          <SeparateLine />
        </div>
        <div className="inline-flex-gap gap8">
          <Button size="default" type="primary" text="Создать" onClick={handleSave} loading={loading} />
          <Button size="default" type="default" text="Отменить" onClick={props.onClose} disabled={loading} />
        </div>
      </div>
    </SidePage>
  )
}

interface IToggleLineProps {
  text: string
  active: boolean
  name: string
  onChange: (val: boolean, name: string) => void
}
const ToggleLine = (props: IToggleLineProps) => {
  return (
    <div className="inline-flex-gap width100 gap16 flex-space-between center">
      <h5 className="text-heading-5">{props.text}</h5>
      <Toggle name={props.name} checked={props.active} onChange={props.onChange} size="large" />
    </div>
  )
}

export default CreateUnplannedMaintenanceBlankSidePage
